<template>
  <div>
    <!-- Mobile Overlay -->
    <div v-if="mobileOpen" class="sidebar-overlay fixed w-full h-full bg-black-alpha-40 z-40 md:hidden"
      @click="emit('close-mobile')"></div>

    <!-- Sidebar -->
    <aside class="surface-card flex flex-column ease-in-out" :class="[
      'md:relative md:translate-x-0',
      isDesktop ? (collapsed ? 'md:w-5rem' : 'md:w-16rem') : '',
      !isDesktop && mobileOpen ? 'mobile-open' : '',
      !isDesktop && !mobileOpen ? 'mobile-closed' : ''
    ]">
      <!-- Logo -->
      <div class="p-3 border-bottom-1 surface-border">
        <div class="flex align-items-center justify-content-center mb-3">
          <div class="flex align-items-center gap-2">
            <div class="nav-bar flex align-items-center w-full sticky top-0 z-5" >
              <img :src="topNavlogo" alt="Illustration" class="topnavbar max-w-full max-h-full object-contain" />
            </div>
          </div>
          <Button v-if="mobileOpen" class="p-button-text p-button-sm md:hidden" @click="emit('close-mobile')">
            <i class="pi pi-times" />
          </Button>
        </div>

      </div>

      <!-- Navigation Items -->
      <div class="flex-1 overflow-y-auto py-2" :class="userTypeClass">
        <ul class="list-none p-0 m-0">
          <li v-for="item in navItems" :key="item.label"
            class="flex align-items-center mb-2 cursor-pointer hover:surface-hover transition-colors rounded-md mx-2"
            :class="{ 'active-item': selectedComponent === item.component }" @click="select(item)">
            <a class="flex px-3 py-3 items-center justify-start w-full h-full text-current menu-link no-underline">
              <i :class="item.icon" class="ml-2 mr-3 text-lg"></i>
              <span class="overflow-hidden Lable whitespace-nowrap"
                :class="[collapsed ? 'w-0 opacity-0 md:opacity-100 hidden' : 'w-auto opacity-100']">
                {{ item.label }}
              </span>
            </a>
          </li>
        </ul>
      </div>

      <!-- Collapse Button (Desktop only) -->
      <div class="flex justify-content-start align-items-start md:block hidden">
        <Button class="p-button-text p-button-sm flex align-items-center justify-content-start" @click="emit('toggle')">
          <i :class="[isDesktop ? (collapsed ? 'pi pi-angle-right' : 'pi pi-angle-left') : '']"></i>
        </Button>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, onMounted } from "vue";
import { useRoute } from "vue-router";
import Button from "primevue/button";
import topNavlogo from "@/assets/topNavLogo.png";
import { useSubscriptionStore } from "@/stores/subscriptionStore";

const subscriptionStore = useSubscriptionStore();
const route = useRoute();

// Subscription-related navigation components that should be hidden for institute-sponsored members
const SUBSCRIPTION_NAV_COMPONENTS = [
  "SubscriptionPage",
  "TutorSubscriptionPage",
  "TutorPaymentMethodPage",
];

const props = defineProps({
  role: { type: String, required: true },
  collapsed: Boolean,
  mobileOpen: Boolean,
  isDesktop: Boolean,
});

const emit = defineEmits(["select", "toggle", "close-mobile"]);

// Navigation state
const navItems = ref([]);
const selectedComponent = ref(null);

// Set navigation items based on role
const setupNavItems = () => {
  const items =
    props.role === "student"
      ? [
        { label: "Profile", component: "StudentProfileForm", icon: "pi pi-user" },
        { label: "Parent", component: "ParentForm", icon: "pi pi-users" },
        { label: "Security", component: "SecurityForm", icon: "pi pi-lock" },
        { label: "Photo", component: "PhotoForm", icon: "pi pi-image" },
        { label: "Subscription", component: "SubscriptionPage", icon: "pi pi-dollar" },
        { label: "Payment", component: "PaymentMethodPage", icon: "pi pi-credit-card" },
      ]
      : props.role === "tutor"
        ? [
          { label: "Profile", component: "TutorProfileForm", icon: "pi pi-user" },
          { label: "Teaching", component: "TutorTeachingForm", icon: "pi pi-id-card" },
          { label: "Qualifications", component: "TutorQualificationForm", icon: "pi pi-trophy" },
          { label: "Experiences", component: "TutorExperiencesForm", icon: "pi pi-clock" },
          { label: "Security", component: "TutorSecurityForm", icon: "pi pi-lock" },
          { label: "Photo", component: "TutorPhotoForm", icon: "pi pi-image" },
          { label: "Subscription", component: "TutorSubscriptionPage", icon: "pi pi-dollar" },
          { label: "Bank Details", component: "TutorBankInfoPage", icon: "pi pi-building-columns" },
          { label: "Payment", component: "TutorPaymentMethodPage", icon: "pi pi-credit-card" },
        ]
        : props.role === "institute"
          ? [
            { label: "Profile", component: "InstituteProfileForm", icon: "pi pi-building" },
            { label: "Security", component: "InstituteSecurityForm", icon: "pi pi-lock" },
            { label: "Photo", component: "InstitutePhotoForm", icon: "pi pi-image" },
            { label: "Subscription", component: "InstituteSubscriptionPage", icon: "pi pi-dollar" },
            { label: "Payment", component: "InstitutePaymentMethodPage", icon: "pi pi-credit-card" },
          ]
          : [];

  // Remove subscription/payment items for institute-sponsored members.
  navItems.value = subscriptionStore.managedByInstitute
    ? items.filter((item) => !SUBSCRIPTION_NAV_COMPONENTS.includes(item.component))
    : items;

  if (navItems.value.length) {
    // Honor a deep-linked tab (e.g. /studentSettings?tab=payment) when present and visible,
    // otherwise fall back to the first available item.
    const requestedTab = (route.query.tab || "").toString().toLowerCase();
    const requested = requestedTab
      ? navItems.value.find(
          (item) =>
            item.component.toLowerCase() === requestedTab ||
            item.label.toLowerCase() === requestedTab
        )
      : null;
    selectedComponent.value = (requested || navItems.value[0]).component;
    emit("select", selectedComponent.value);
  }
};

// Initialize nav items
setupNavItems();

// Watch role change
watch(() => props.role, () => setupNavItems());

// Watch for changes in managedByInstitute to update nav items accordingly
watch(() => subscriptionStore.managedByInstitute, () => setupNavItems());

onMounted(() => {
  subscriptionStore.fetchMy();
});

// Computed class for role-based sidebar
const userTypeClass = computed(() => {
  switch (props.role) {
    case "student":
      return "menu-student";
    case "tutor":
      return "menu-tutor";
    case "institute":
      return "menu-institution";
    default:
      return "menu-default";
  }
});

// Methods
const select = (item) => {
  selectedComponent.value = item.component;
  emit("select", item.component);
};
</script>

<style scoped>
.topnavbar {
  width: 160px;
  height: auto;
}

.surface-card {
  background-color: var(--gray-400) !important;
  height: 100dvh;
  transition: transform 0.8s ease-in-out !important;
}

.sidebar-overlay {
  backdrop-filter: blur(0.5px) !important;
  z-index: 50 !important;
}

li {
  border-radius: 0.375rem;
  margin: 0 0.5rem;
}

/* === Mobile Sidebar States === */
.mobile-open {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  z-index: 50 !important;
  width: 16rem !important;
  height: 100% !important;
  transform: translateX(0) !important;
}

.mobile-closed {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  z-index: 50 !important;
  width: 16rem !important;
  height: 100% !important;
  transform: translateX(-100%) !important;
}

.menu-link {
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
}

.menu-default li:hover {
  background-color: var(--teal-400);
}

.menu-default .active-item {
  background-color: var(--teal-400);
}

/* Student */
.menu-student li:hover {
  background-color: var(--orange-50) !important;
}

.menu-student .active-item {
  background-color: var(--orange-50);
}

/* Tutor */
.menu-tutor li:hover {
  background-color: var(--teal-400) !important;
}

.menu-tutor .active-item {
  background-color: var(--teal-400);
}

/* Institution */
.menu-institution li:hover {
  background-color: var(--yellow-50) !important;
}

.menu-institution .active-item {
  background-color: var(--yellow-50) !important;
}

.p-button-text {
  color: var(--gray-600);
}

@media (max-width: 767px) {
  aside {
    z-index: 50 !important;
  }

  .title {
    font-size: 18px;

  }
}
</style>
